import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { formatPrice } from "@/lib/format";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, X, Star, Upload, Eye, ChevronUp, ChevronDown } from "lucide-react";

const EMPTY = {
  name: "", price: 0, old_price: "", category: "", stock: 0,
  description: "", images: [], featured: false, active: true, variant_label: "Couleur", variants_text: "", variant_stock: {},
};

function ProductView({ product: p, categories, onClose, onEdit }) {
  const cat = categories.find((c) => c.slug === p.category)?.name || "Sans catégorie";
  const variants = p.variants || [];
  return (
    <div className="fixed inset-0 z-[95] flex items-center justify-center bg-teal-deep/40 backdrop-blur-sm p-4" data-testid="admin-product-view-modal" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div data-lenis-prevent className="w-full max-w-2xl max-h-[90vh] overflow-y-auto overscroll-contain bg-white rounded-3xl p-6 md:p-8">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-mauve">{cat}</p>
            <h2 className="font-serif text-2xl text-teal-dark mt-1">{p.name}</h2>
          </div>
          <button type="button" data-testid="admin-product-view-close" onClick={onClose} aria-label="Fermer"><X size={22} className="text-mauve" /></button>
        </div>
        {(p.images || []).length > 0 && (
          <div className="flex flex-wrap gap-3 mb-6">
            {p.images.map((img) => <img key={img} src={img} alt="" className="h-28 w-24 rounded-2xl object-cover border border-line" />)}
          </div>
        )}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div className="rounded-2xl bg-cream-light p-3"><p className="text-xs text-mauve">Prix</p><p className="font-semibold text-teal">{formatPrice(p.price)}</p></div>
          <div className="rounded-2xl bg-cream-light p-3"><p className="text-xs text-mauve">Ancien prix</p><p className="font-medium">{p.old_price ? formatPrice(p.old_price) : "—"}</p></div>
          <div className="rounded-2xl bg-cream-light p-3"><p className="text-xs text-mauve">Stock total</p><p data-testid="admin-product-view-stock" className={`font-medium ${p.stock <= 3 ? "text-[#b47b2e]" : ""}`}>{p.stock}</p></div>
          <div className="rounded-2xl bg-cream-light p-3"><p className="text-xs text-mauve">Statut</p><p className="font-medium">{p.active ? "Visible" : "Masqué"}{p.featured ? " · ⭐ Phare" : ""}</p></div>
        </div>
        {variants.length > 0 && (
          <div className="mt-5">
            <p className="text-sm font-medium text-ink mb-2">Stock par {(p.variant_label || "option").toLowerCase()}</p>
            <div className="flex flex-wrap gap-2">
              {variants.map((v) => (
                <span key={v} className="rounded-full border border-line bg-cream-light px-3 py-1.5 text-sm">{v} <span className="font-semibold text-teal">· {p.variant_stock?.[v] ?? 0}</span></span>
              ))}
            </div>
          </div>
        )}
        {p.description && (
          <div className="mt-5">
            <p className="text-sm font-medium text-ink mb-1">Description</p>
            <p className="text-sm text-ink/80 whitespace-pre-line leading-relaxed">{p.description}</p>
          </div>
        )}
        <p className="mt-5 text-xs text-mauve">Adresse boutique : /produit/{p.slug}</p>
        <div className="mt-8 flex flex-col-reverse sm:flex-row gap-3">
          <button type="button" data-testid="admin-product-view-close-btn" onClick={onClose} className="flex-1 rounded-full border border-line py-3.5 font-medium text-ink hover:bg-cream-light transition-colors">Fermer</button>
          <button type="button" data-testid="admin-product-view-edit" onClick={onEdit} className="flex-1 rounded-full bg-teal py-3.5 font-medium text-cream hover:bg-teal-dark transition-colors flex items-center justify-center gap-2"><Pencil size={15} /> Modifier</button>
        </div>
      </div>
    </div>
  );
}

export default function Products() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null); // null | {} | product
  const [viewing, setViewing] = useState(null);
  const [saving, setSaving] = useState(false);
  const openEdit = (p) => setEditing({ ...EMPTY, ...p, variants_text: (p.variants || []).join(", ") });

  useEffect(() => {
    if (!editing && !viewing) return;
    const onKey = (e) => { if (e.key === "Escape") { setEditing(null); setViewing(null); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [editing, viewing]);

  const load = () => {
    api.get("/admin/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const move = async (i, dir) => {
    const next = [...products];
    [next[i], next[i + dir]] = [next[i + dir], next[i]];
    setProducts(next);
    try {
      await api.post("/admin/products/reorder", { ids: next.map((p) => p.id) });
    } catch (e) {
      toast.error(formatApiError(e));
      load();
    }
  };

  const patch = async (id, data, msg) => {
    try {
      await api.patch(`/admin/products/${id}`, data);
      if (msg) toast.success(msg);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const removeProduct = async (p) => {
    if (!window.confirm(`Supprimer définitivement « ${p.name} » ?`)) return;
    try {
      await api.delete(`/admin/products/${p.id}`);
      toast.success("Produit supprimé");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...editing,
        price: Number(editing.price) || 0,
        old_price: editing.old_price !== "" && editing.old_price != null ? Number(editing.old_price) : null,
        stock: Number(editing.stock) || 0,
        variants: (editing.variants_text || "").split(",").map((v) => v.trim()).filter(Boolean),
        variant_stock: Object.fromEntries(Object.entries(editing.variant_stock || {}).map(([k, v]) => [k, Number(v) || 0])),
      };
      delete payload.variants_text;
      if (editing.id) {
        await api.put(`/admin/products/${editing.id}`, payload);
        toast.success("Produit mis à jour");
      } else {
        await api.post("/admin/products", payload);
        toast.success("Produit créé");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd);
      setEditing((ed) => ({ ...ed, images: [...(ed.images || []), data.url] }));
      toast.success("Image téléversée");
    } catch (err) {
      toast.error(formatApiError(err));
    }
    e.target.value = "";
  };

  const addCategory = async () => {
    const name = window.prompt("Nom de la nouvelle catégorie :");
    if (!name) return;
    try {
      await api.post("/admin/categories", { name });
      toast.success("Catégorie ajoutée");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const removeCategory = async (c) => {
    if (!window.confirm(`Supprimer la catégorie « ${c.name} » ?`)) return;
    try {
      await api.delete(`/admin/categories/${c.id}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="admin-products-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-serif text-3xl md:text-4xl text-teal-dark">Produits & Stock</h1>
          <p className="text-sm text-ink/60 mt-1">Ajoutez vos produits, ajustez les prix et gérez le stock en direct.</p>
        </div>
        <button data-testid="admin-product-new" onClick={() => setEditing({ ...EMPTY })} className="flex items-center gap-2 rounded-full bg-teal px-5 py-2.5 text-sm font-medium text-cream hover:bg-teal-dark transition-colors">
          <Plus size={16} /> Nouveau produit
        </button>
      </div>

      <div className="mt-6 bg-white rounded-3xl border border-line overflow-x-auto">
        <table className="w-full text-sm min-w-[760px]">
          <thead>
            <tr className="text-left text-mauve font-mono text-[11px] uppercase tracking-wider border-b border-line">
              <th className="pl-4 py-4">Ordre</th>
              <th className="px-5 py-4">Produit</th>
              <th className="px-3 py-4">Prix</th>
              <th className="px-3 py-4">Stock</th>
              <th className="px-3 py-4">Phare</th>
              <th className="px-3 py-4">Visible</th>
              <th className="px-3 py-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {products.map((p, i) => (
              <tr key={p.id} data-testid={`admin-product-row-${p.slug}`} className="border-b border-line/60 last:border-0">
                <td className="pl-4 py-3">
                  <div className="flex flex-col">
                    <button data-testid={`admin-product-up-${p.slug}`} disabled={i === 0} onClick={() => move(i, -1)} className="p-1 text-mauve hover:text-teal disabled:opacity-25" aria-label="Monter"><ChevronUp size={16} /></button>
                    <button data-testid={`admin-product-down-${p.slug}`} disabled={i === products.length - 1} onClick={() => move(i, 1)} className="p-1 text-mauve hover:text-teal disabled:opacity-25" aria-label="Descendre"><ChevronDown size={16} /></button>
                  </div>
                </td>
                <td className="px-5 py-3">
                  <div className="flex items-center gap-3">
                    {p.images?.[0] && <img src={p.images[0]} alt="" className="h-12 w-10 rounded-lg object-cover" />}
                    <div>
                      <p className="font-medium text-teal-dark">{p.name}</p>
                      <p className="text-xs text-mauve">{categories.find((c) => c.slug === p.category)?.name || p.category || "—"}</p>
                    </div>
                  </div>
                </td>
                <td className="px-3 py-3 whitespace-nowrap text-teal font-medium">{formatPrice(p.price)}</td>
                <td className="px-3 py-3">
                  <input
                    data-testid={`admin-stock-input-${p.slug}`}
                    type="number"
                    min="0"
                    defaultValue={p.stock}
                    disabled={(p.variants || []).length > 0}
                    title={(p.variants || []).length > 0 ? "Stock géré par option dans la fiche" : ""}
                    onBlur={(e) => {
                      const v = Number(e.target.value);
                      if (v !== p.stock) patch(p.id, { stock: v }, "Stock mis à jour");
                    }}
                    className={`w-20 rounded-xl border px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-mint/40 ${p.stock <= 3 ? "border-[#b47b2e]/50 bg-tan/20" : "border-line bg-cream-light"}`}
                  />
                </td>
                <td className="px-3 py-3">
                  <button data-testid={`admin-featured-toggle-${p.slug}`} onClick={() => patch(p.id, { featured: !p.featured })} aria-label="Basculer produit phare">
                    <Star size={18} className={p.featured ? "fill-tan text-tan" : "text-line"} />
                  </button>
                </td>
                <td className="px-3 py-3">
                  <button
                    data-testid={`admin-active-toggle-${p.slug}`}
                    onClick={() => patch(p.id, { active: !p.active })}
                    className={`relative h-6 w-11 rounded-full transition-colors ${p.active ? "bg-mint" : "bg-line"}`}
                    aria-label="Basculer la visibilité"
                  >
                    <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${p.active ? "left-[22px]" : "left-0.5"}`} />
                  </button>
                </td>
                <td className="px-3 py-3 text-right whitespace-nowrap">
                  <button data-testid={`admin-product-view-${p.slug}`} onClick={() => setViewing(p)} className="p-2 text-mauve hover:text-teal" aria-label="Voir"><Eye size={16} /></button>
                  <button data-testid={`admin-product-edit-${p.slug}`} onClick={() => openEdit(p)} className="p-2 text-teal hover:text-teal-dark" aria-label="Modifier"><Pencil size={16} /></button>
                  <button data-testid={`admin-product-delete-${p.slug}`} onClick={() => removeProduct(p)} className="p-2 text-[#b04a4a] hover:opacity-70" aria-label="Supprimer"><Trash2 size={16} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {products.length === 0 && <p className="p-8 text-center text-ink/60">Aucun produit — cliquez sur « Nouveau produit » pour commencer.</p>}
      </div>

      <div className="mt-6 bg-white rounded-3xl border border-line p-6">
        <h2 className="font-serif text-xl text-teal-dark mb-3">Catégories</h2>
        <div className="flex flex-wrap gap-2">
          {categories.map((c) => (
            <span key={c.id} className="flex items-center gap-2 rounded-full bg-cream px-4 py-1.5 text-sm text-teal-dark">
              {c.name}
              <button data-testid={`admin-category-delete-${c.slug}`} onClick={() => removeCategory(c)} aria-label={`Supprimer ${c.name}`} className="text-mauve hover:text-[#b04a4a]"><X size={13} /></button>
            </span>
          ))}
          <button data-testid="admin-category-new" onClick={addCategory} className="flex items-center gap-1.5 rounded-full border border-dashed border-mauve/60 px-4 py-1.5 text-sm text-mauve hover:text-teal hover:border-teal transition-colors">
            <Plus size={13} /> Ajouter
          </button>
        </div>
      </div>

      {viewing && (
        <ProductView
          product={viewing}
          categories={categories}
          onClose={() => setViewing(null)}
          onEdit={() => { const p = viewing; setViewing(null); openEdit(p); }}
        />
      )}

      {editing && (
        <div className="fixed inset-0 z-[95] flex items-center justify-center bg-teal-deep/40 backdrop-blur-sm p-4" data-testid="admin-product-modal" onMouseDown={(e) => { if (e.target === e.currentTarget) setEditing(null); }}>
          <form onSubmit={save} data-lenis-prevent className="w-full max-w-2xl max-h-[90vh] overflow-y-auto overscroll-contain bg-white rounded-3xl p-6 md:p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-2xl text-teal-dark">{editing.id ? "Modifier le produit" : "Nouveau produit"}</h2>
              <button type="button" data-testid="admin-product-modal-close" onClick={() => setEditing(null)} aria-label="Fermer"><X size={22} className="text-mauve" /></button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="md:col-span-2 text-sm font-medium text-ink">
                Nom du produit *
                <input required data-testid="admin-product-name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="mt-1.5 w-full rounded-2xl border border-line bg-cream-light px-4 py-3 text-sm outline-none focus:border-teal" />
              </label>
              <label className="text-sm font-medium text-ink">
                Prix (FCFA) *
                <input required type="number" min="0" data-testid="admin-product-price" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} className="mt-1.5 w-full rounded-2xl border border-line bg-cream-light px-4 py-3 text-sm outline-none focus:border-teal" />
              </label>
              <label className="text-sm font-medium text-ink">
                Ancien prix (promo, optionnel)
                <input type="number" min="0" data-testid="admin-product-old-price" value={editing.old_price || ""} onChange={(e) => setEditing({ ...editing, old_price: e.target.value })} className="mt-1.5 w-full rounded-2xl border border-line bg-cream-light px-4 py-3 text-sm outline-none focus:border-teal" />
              </label>
              <label className="text-sm font-medium text-ink">
                Catégorie
                <select data-testid="admin-product-category" value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className="mt-1.5 w-full rounded-2xl border border-line bg-cream-light px-4 py-3 text-sm outline-none focus:border-teal">
                  <option value="">Sans catégorie</option>
                  {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                </select>
              </label>
              {(editing.variants_text || "").trim() ? (
                <div className="text-sm font-medium text-ink">
                  Stock par option
                  <div className="mt-1.5 grid grid-cols-2 gap-2">
                    {editing.variants_text.split(",").map((v) => v.trim()).filter(Boolean).map((v) => (
                      <label key={v} className="flex items-center gap-2 rounded-2xl border border-line bg-cream-light px-3 py-2 text-sm">
                        <span className="flex-1 truncate">{v}</span>
                        <input type="number" min="0" data-testid={`admin-product-variant-stock-${v.toLowerCase().replace(/\s+/g, "-")}`} value={editing.variant_stock?.[v] ?? 0} onChange={(e) => setEditing({ ...editing, variant_stock: { ...(editing.variant_stock || {}), [v]: e.target.value } })} className="w-16 rounded-lg border border-line bg-white px-2 py-1 text-right outline-none focus:border-teal" />
                      </label>
                    ))}
                  </div>
                </div>
              ) : (
                <label className="text-sm font-medium text-ink">
                  Stock
                  <input type="number" min="0" data-testid="admin-product-stock" value={editing.stock} onChange={(e) => setEditing({ ...editing, stock: e.target.value })} className="mt-1.5 w-full rounded-2xl border border-line bg-cream-light px-4 py-3 text-sm outline-none focus:border-teal" />
                </label>
              )}
              <label className="text-sm font-medium text-ink">
                Options (séparées par des virgules)
                <input data-testid="admin-product-variants" value={editing.variants_text} onChange={(e) => setEditing({ ...editing, variants_text: e.target.value })} placeholder="Vert, Rose" className="mt-1.5 w-full rounded-2xl border border-line bg-cream-light px-4 py-3 text-sm outline-none focus:border-teal" />
              </label>
              <label className="text-sm font-medium text-ink">
                Nom de l'option
                <input data-testid="admin-product-variant-label" value={editing.variant_label} onChange={(e) => setEditing({ ...editing, variant_label: e.target.value })} placeholder="Couleur, Taille…" className="mt-1.5 w-full rounded-2xl border border-line bg-cream-light px-4 py-3 text-sm outline-none focus:border-teal" />
              </label>
              <label className="md:col-span-2 text-sm font-medium text-ink">
                Description
                <textarea rows={4} data-testid="admin-product-description" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="mt-1.5 w-full rounded-2xl border border-line bg-cream-light px-4 py-3 text-sm outline-none focus:border-teal" />
              </label>
              <div className="md:col-span-2">
                <p className="text-sm font-medium text-ink">Images</p>
                <div className="mt-2 flex flex-wrap gap-3">
                  {(editing.images || []).map((img, i) => (
                    <div key={img} className="relative">
                      <img src={img} alt="" className="h-16 w-14 rounded-xl object-cover border border-line" />
                      <button type="button" onClick={() => setEditing({ ...editing, images: editing.images.filter((_, j) => j !== i) })} className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-[#b04a4a] text-white flex items-center justify-center" aria-label="Retirer l'image"><X size={12} /></button>
                    </div>
                  ))}
                  <label data-testid="admin-product-upload" className="flex h-16 w-14 cursor-pointer flex-col items-center justify-center rounded-xl border border-dashed border-mauve/60 text-mauve hover:text-teal hover:border-teal transition-colors">
                    <Upload size={16} />
                    <span className="text-[9px] mt-1">Envoyer</span>
                    <input type="file" accept="image/*" onChange={uploadImage} className="hidden" />
                  </label>
                </div>
              </div>
              <div className="md:col-span-2 flex flex-wrap gap-6">
                <label className="flex items-center gap-2 text-sm text-ink">
                  <input type="checkbox" data-testid="admin-product-featured" checked={!!editing.featured} onChange={(e) => setEditing({ ...editing, featured: e.target.checked })} className="h-4 w-4 accent-teal" />
                  Produit phare (mis en avant sur l'accueil)
                </label>
                <label className="flex items-center gap-2 text-sm text-ink">
                  <input type="checkbox" data-testid="admin-product-active" checked={!!editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} className="h-4 w-4 accent-teal" />
                  Visible dans la boutique
                </label>
              </div>
            </div>
            <div className="mt-8 flex flex-col-reverse sm:flex-row gap-3">
              <button type="button" data-testid="admin-product-cancel" onClick={() => setEditing(null)} className="flex-1 rounded-full border border-line py-3.5 font-medium text-ink hover:bg-cream-light transition-colors">
                Annuler
              </button>
              <button type="submit" data-testid="admin-product-save" disabled={saving} className="flex-[2] rounded-full bg-teal py-3.5 font-medium text-cream hover:bg-teal-dark transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                {saving && <Loader2 size={16} className="animate-spin" />}
                {saving ? "Enregistrement…" : "Enregistrer"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
